import { ArrowRight, LogOut, ShieldCheck } from "lucide-react";
import type { ThemeClasses } from "@/ui/theme/ThemeContext";

type AuthenticatedAuthNoticeProps = {
  activeView: "login" | "signup";
  theme: ThemeClasses;
  title: string;
  description: string;
  continueLabel: string;
  signOutLabel: string;
  onContinue: () => void;
  onSignOut: () => void;
};

export const AuthenticatedAuthNotice = ({
  activeView,
  theme: _theme,
  title,
  description,
  continueLabel,
  signOutLabel,
  onContinue,
  onSignOut,
}: AuthenticatedAuthNoticeProps) => (
  <div
    data-app-ready="true"
    data-active-view={activeView}
    className="flex min-h-screen items-center justify-center overflow-x-hidden bg-[linear-gradient(135deg,#f8fafc_0%,#eef2ff_42%,#ecfeff_100%)] px-5 py-8 text-zinc-950 dark:bg-[linear-gradient(135deg,#121212_0%,#171717_50%,#0f1a19_100%)] dark:text-white sm:px-8"
  >
    <div
      className="relative w-full max-w-sm min-w-0 overflow-hidden rounded-lg border border-zinc-200/80 bg-white/[0.78] p-6 text-center shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/[0.10] dark:bg-white/[0.06] dark:shadow-[0_28px_110px_rgba(0,0,0,0.34)] sm:p-8"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2563eb,#06b6d4_52%,#10b981)]" aria-hidden="true" />
      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] text-white shadow-[0_14px_34px_rgba(37,99,235,0.22)]" aria-hidden="true">
        <ShieldCheck className="h-5 w-5" />
      </div>
      <h1 className="break-words text-3xl font-semibold leading-tight text-zinc-950 dark:text-white">
        {title}
      </h1>
      <p className="mt-3 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
        {description}
      </p>
      <div className="mt-6 grid min-w-0 gap-3">
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] px-4 py-2 font-semibold text-white shadow-[0_14px_34px_rgba(37,99,235,0.34)] transition-all duration-300 hover:shadow-[0_18px_44px_rgba(6,182,212,0.34)] focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950"
        >
          {continueLabel}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onSignOut}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border border-zinc-300/80 bg-white/70 px-4 py-2 font-semibold text-zinc-950 transition-all duration-300 hover:border-zinc-400 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:border-white/[0.14] dark:bg-white/[0.05] dark:text-white dark:hover:border-white/25 dark:hover:bg-white/[0.09] dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          {signOutLabel}
        </button>
      </div>
    </div>
  </div>
);
